// About.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './About.css';
import Skill from '../Skills/Skills';

const About = () => {
  const [activeTab, setActiveTab] = useState('intro');
  const [animatedCounters, setAnimatedCounters] = useState(false);
  const navigate = useNavigate();

  // Typing effect for hero section
  const roles = [
    "📱 Mobile App Developer",
    "💻 Front-end Web Developer",
    "📊 Data Science Enthusiast",
    "🦋 Flutter Expert"
  ];
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [currentRole, setCurrentRole] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);

  useEffect(() => {
    const handleTyping = () => {
      const current = currentRoleIndex % roles.length;
      const fullText = roles[current];

      if (isDeleting) {
        setCurrentRole(fullText.substring(0, currentRole.length - 1));
        setTypingSpeed(typingSpeed / 1.5);
      } else {
        setCurrentRole(fullText.substring(0, currentRole.length + 1));
      }

      if (!isDeleting && currentRole === fullText) {
        setTimeout(() => setIsDeleting(true), 1000);
      } else if (isDeleting && currentRole === '') {
        setIsDeleting(false);
        setCurrentRoleIndex((currentRoleIndex + 1) % roles.length);
        setTypingSpeed(150);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [currentRole, isDeleting, currentRoleIndex, roles, typingSpeed]);

  // Counter animation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const countersSection = document.querySelector('.achievements-section');
      if (countersSection && window.scrollY + window.innerHeight > countersSection.offsetTop + 200) {
        setAnimatedCounters(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Skills data
  const skills = [
    { name: 'Flutter/Dart', level: 90 },
    { name: 'Python', level: 85 },
    { name: 'React JS', level: 80 },
    { name: 'Data Science', level: 75 },
    { name: 'MySQL/MongoDB', level: 85 },
    { name: 'Machine Learning', level: 70 },
    { name: 'RESTful APIs', level: 80 },
    { name: 'Git/GitHub', level: 90 }
  ];

  // Timeline data
  const timelineData = [
    {
      year: '2022 - 2025',
      title: 'B.S.C. (IT) in Computer Science',
      subtitle: 'GLS University, Gujarat',
      description: 'CGPA: 8.30',
      icon: '🎓'
    },
    {
      year: '2024',
      title: 'Faculty of C and C++',
      subtitle: 'Success Computer Class, Ahmedabad',
      description: 'Delivered structured lectures and hands-on training',
      icon: '👨‍🏫'
    },
    {
      year: 'Jan 2025',
      title: 'Flutter Developer (Freelance)',
      subtitle: 'Shree Hanumanji App',
      description: 'Created a multilingual scripture app with 700+ shloks',
      icon: '💼'
    },
    {
      year: 'Dec 2024 - Feb 2025',
      title: 'AR/VR E-Commerce Website',
      subtitle: 'M.E.R.N. Stack Project',
      description: 'Designed and deployed a full-stack AR/VR web application',
      icon: '🌐'
    },
    {
      year: 'June 2025 – Dec 2025',
      title: 'Data Science Pro Program',
      subtitle: 'Fingertips Institute',
      description: 'Score: A+ (approx)',
      icon: '📊'
    }
  ];

  // Achievements data
  const achievements = [
    { id: 1, title: 'Projects Completed', count: 8, icon: '🚀' },
    { id: 2, title: 'Certifications', count: 3, icon: '🏆' },
    { id: 3, title: 'Happy Clients', count: 5, icon: '😊' },
    { id: 4, title: 'Years Learning', count: 3, icon: '📚' }
  ];

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      text: "Ritesh developed our scripture app with great attention to detail. The multilingual support and offline functionality exceeded our expectations.",
      author: "Religious Organization Client",
      role: "App Project"
    },
    {
      id: 2,
      text: "As a student of Ritesh's C++ class, I found his teaching methods very effective. He has a knack for explaining complex concepts in simple terms.",
      author: "Student",
      role: "Success Computer Class"
    },
    {
      id: 3,
      text: "The AR/VR e-commerce project led by Ritesh was innovative and well-executed. He demonstrated strong technical and team leadership skills.",
      author: "Project Team Member",
      role: "GLS University"
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Handler for navigating to the projects page
  const handleProjectsClick = (e) => {
    e.preventDefault();
    navigate('/projects');
  };

  return (
    <div className="about-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Gajjar Ritesh Rajeshbhai</h1>
          <h2>
            I'm a <span className="typing-text">{currentRole}</span><span className="cursor">|</span>
          </h2>
          <p className="hero-description">
            A passionate developer creating innovative solutions with Flutter, React, and Data Science. 
            I thrive on turning complex problems into simple, beautiful, and intuitive solutions.
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => navigate('/skills')}>View My Skills</button>
            <button
              className="btn-secondary"
              onClick={handleProjectsClick}
            >
              See Projects
            </button>
          </div>
        </div>
        <div className="hero-image">
          <div className="floating-card">
            <div className="card-content">
              <h3>Fun Fact!</h3>
              <p>I once built a fully functional app in 48 hours during a coding marathon!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section - reuse Skills component */}
      <section className="skills-section" id="skills">
        <h2 className="section-title">Technical Skills</h2>
        <Skill />
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <h2 className="section-title">My Journey</h2>
        <div className="timeline">
          {timelineData.map((item, index) => (
            <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
              <div className="timeline-content">
                <div className="timeline-year">{item.year}</div>
                <div className="timeline-icon">{item.icon}</div>
                <h3 className="timeline-title">{item.title}</h3>
                <h4 className="timeline-subtitle">{item.subtitle}</h4>
                <p className="timeline-description">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Achievements Section */}
      <section className="achievements-section">
        <h2 className="section-title">Achievements & Milestones</h2>
        <div className="achievements-grid">
          {achievements.map((achievement) => (
            <div key={achievement.id} className="achievement-card">
              <div className="achievement-icon">{achievement.icon}</div>
              <div className="achievement-content">
                <h3 className="achievement-count">
                  {animatedCounters ? achievement.count : 0}+
                </h3>
                <p className="achievement-title">{achievement.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2 className="section-title">What Others Say</h2>
        <div className="testimonial-container">
          <div className="testimonial-content">
            <p className="testimonial-text">"{testimonials[currentTestimonial].text}"</p>
            <div className="testimonial-author">
              <h4>{testimonials[currentTestimonial].author}</h4>
              <p>{testimonials[currentTestimonial].role}</p>
            </div>
          </div>
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                onClick={() => setCurrentTestimonial(index)}
              ></span>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <h2>Ready to Bring Your Ideas to Life?</h2>
        <p>Let's collaborate to create something amazing together</p>
        <div className="cta-buttons">
          <button
            className="btn-primary"
            onClick={handleProjectsClick}
          >
            View My Projects
          </button>
          <button
            className="btn-secondary"
            onClick={() => window.location.href = "mailto:ritesh.work.1510@gmail.com"}
          >
            Get In Touch
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;